import { useEffect, useRef, useState } from 'react'
import { sseManager } from './DocumentStatusStreamManager'
import type { DocumentStatusEvent } from './DocumentStatusStreamManager'

export type { DocumentStatusEvent }

interface UseDocumentStatusStreamOptions {
  enabled?: boolean
  onStatusUpdate?: (event: DocumentStatusEvent) => void
  onError?: (error: Event) => void
  onDisconnected?: () => void
}

interface UseDocumentStatusStreamResult {
  lastEvent: DocumentStatusEvent | null
  isConnected: boolean
  error: Event | null
}

export const useDocumentStatusStream = (
  options: UseDocumentStatusStreamOptions = {}
): UseDocumentStatusStreamResult => {
  const { enabled = true, onStatusUpdate, onError, onDisconnected } = options
  const [lastEvent, setLastEvent] = useState<DocumentStatusEvent | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [error, setError] = useState<Event | null>(null)

  const onStatusUpdateRef = useRef(onStatusUpdate)
  const onErrorRef = useRef(onError)
  const onDisconnectedRef = useRef(onDisconnected)

  useEffect(() => {
    onStatusUpdateRef.current = onStatusUpdate
    onErrorRef.current = onError
    onDisconnectedRef.current = onDisconnected
  }, [onStatusUpdate, onError, onDisconnected])

  useEffect(() => {
    if (!enabled) {
      setIsConnected(false)
      return
    }

    let unsubscribe: (() => void) | null = null

    const setupSubscription = async () => {
      try {
        unsubscribe = await sseManager.subscribe(
          (event) => {
            setLastEvent(event)
            setIsConnected(true)
            setError(null)
            onStatusUpdateRef.current?.(event)
          },
          (err) => {
            setError(err)
            setIsConnected(false)
            onErrorRef.current?.(err)
          },
          () => {
            setIsConnected(false)
            onDisconnectedRef.current?.()
          }
        )

        setIsConnected(sseManager.isConnected())
      } catch (err) {
        console.error('SSE subscription failed:', err)
      }
    }

    setupSubscription()

    return () => {
      unsubscribe?.()
      setIsConnected(sseManager.isConnected())
    }
  }, [enabled])

  return {
    lastEvent,
    isConnected,
    error,
  }
}
